import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { leaveService } from "@/features/leave/services/leaveService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
} from "@/shared/services/uploadService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { CompanySearchSelect } from "@/shared/components/ui/CompanySearchSelect";
import { UserSearchSelect } from "@/shared/components/ui/UserSearchSelect";
import type { LeaveField, LeaveTemplate, LeaveTemplateSummary } from "@/features/leave/types/leave";

const OFFICER_TYPES = ["officer", "admin"];

type AppStep = "select_template" | "fill_form";

export const LeaveApplicationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { userData } = useAuth();

  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  // If a templateId is passed in the URL, skip the selection step
  const urlTemplateId = searchParams.get("templateId") ?? undefined;

  const [step, setStep] = useState<AppStep>(urlTemplateId ? "fill_form" : "select_template");

  // Template selection state
  const [templateList, setTemplateList] = useState<LeaveTemplateSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [, setSelectedTemplateId] = useState<string | undefined>(urlTemplateId);

  // Form fill state
  const [template, setTemplate] = useState<LeaveTemplate | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Pending file selections: fieldId → File (uploaded on submit)
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});

  // On Behalf state
  const [isOnBehalf, setIsOnBehalf] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");

  // ── Load template list ──────────────────────────────────────────────────

  const loadTemplateList = useCallback(async (companyId?: string) => {
    try {
      setListLoading(true);
      const data = await leaveService.getTemplates(companyId);
      setTemplateList(data);
      // Auto-select if exactly one template
      if (data.length === 1 && !urlTemplateId) {
        setSelectedTemplateId(data[0].template_id);
        loadTemplateById(data[0].template_id);
        setStep("fill_form");
      }
    } catch (err) {
      console.error("Failed to load template list:", err);
      setTemplateList([]);
    } finally {
      setListLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load specific template ──────────────────────────────────────────────

  const loadTemplateById = useCallback(async (templateId: string, companyId?: string) => {
    try {
      setLoading(true);
      const data = await leaveService.getTemplate(companyId, templateId);
      setTemplate(data);
      const initial: Record<string, string> = {};
      (data.fields || []).forEach((f) => { initial[f.id] = ""; });
      setAnswers(initial);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setTemplate(null);
      } else {
        console.error("Failed to load leave template:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Initial load ────────────────────────────────────────────────────────

  useEffect(() => {
    if (urlTemplateId) {
      // Directly load the form when templateId is in the URL
      loadTemplateById(urlTemplateId);
    } else if (!isOnBehalf) {
      loadTemplateList();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Select template from list ───────────────────────────────────────────

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    loadTemplateById(templateId, isOnBehalf ? selectedCompanyId : undefined);
    setStep("fill_form");
  };

  // ── On Behalf handlers ──────────────────────────────────────────────────

  const handleCompanyChange = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setSelectedUserId("");
    setTemplate(null);
    setTemplateList([]);
    setStep("select_template");
    if (companyId) {
      loadTemplateList(companyId);
    }
  };

  const handleOnBehalfToggle = (checked: boolean) => {
    setIsOnBehalf(checked);
    setSelectedCompanyId("");
    setSelectedUserId("");
    setTemplate(null);
    setTemplateList([]);
    setStep("select_template");
    if (!checked) {
      loadTemplateList();
    }
  };

  // ── Validation ──────────────────────────────────────────────────────────

  const validateField = (field: LeaveField, value: string): string => {
    if (field.type === "file") {
      if (field.required && !value.trim()) return `${field.label} is required.`;
      return "";
    }
    if (field.required && !value.trim()) return `${field.label} is required.`;
    if (field.type === "number" && value && isNaN(Number(value))) {
      return `${field.label} must be a valid number.`;
    }
    return "";
  };

  const validate = (): boolean => {
    if (!template) return false;
    const newErrors: Record<string, string> = {};
    let valid = true;

    template.fields.forEach((field) => {
      const err = validateField(field, answers[field.id] || "");
      if (err) { newErrors[field.id] = err; valid = false; }
    });

    if (isOnBehalf && !selectedUserId) {
      newErrors["_targetUser"] = t("leave.target_user_required");
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // ── Field handlers ──────────────────────────────────────────────────────

  const handleChange = (fieldId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors((prev) => { const u = { ...prev }; delete u[fieldId]; return u; });
    }
  };

  const MAX_FILE_SIZE = 15 * 1024 * 1024;

  const handleFileChange = (fieldId: string, file: File | null) => {
    if (!file) {
      setPendingFiles((prev) => { const u = { ...prev }; delete u[fieldId]; return u; });
      setAnswers((prev) => ({ ...prev, [fieldId]: "" }));
    } else {
      if (file.size > MAX_FILE_SIZE) {
        setErrors((prev) => ({ ...prev, [fieldId]: t("leave.file_too_large") }));
        return;
      }
      setPendingFiles((prev) => ({ ...prev, [fieldId]: file }));
      setAnswers((prev) => ({ ...prev, [fieldId]: "__pending__" }));
    }
    if (errors[fieldId]) {
      setErrors((prev) => { const u = { ...prev }; delete u[fieldId]; return u; });
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate() || !template) return;

    setSubmitting(true);
    try {
      const resolvedAnswers = { ...answers };
      for (const [fieldId, file] of Object.entries(pendingFiles)) {
        const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(
          file.name, file.type, "leave", template.template_id,
        );
        await uploadToS3Direct(uploadUrl, file, file.type);
        const confirmed = await confirmAttachmentInDB({
          relation_type: "leave",
          relation_id: String(template.template_id),
          s3_key: s3Key,
          s3_bucket: bucketName,
          display_name: file.name,
          file_type: file.type,
          file_size: file.size,
        });
        resolvedAnswers[fieldId] = String(confirmed.attachment_id ?? confirmed.id ?? "");
      }

      await leaveService.submit(
        template.template_id,
        resolvedAnswers,
        isOnBehalf && selectedUserId ? selectedUserId : undefined,
      );
      setSubmitted(true);
    } catch (err: any) {
      console.error("Failed to submit leave:", err);
      setSubmitError(err?.response?.data?.error || "Failed to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render field ────────────────────────────────────────────────────────

  const renderField = (field: LeaveField) => {
    const value = answers[field.id] || "";
    const error = errors[field.id];
    const baseInputClass = `w-full px-3 py-2.5 md:py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
      error ? "border-red-300 focus:ring-red-500" : "border-gray-300"
    }`;
    const pendingFile = pendingFiles[field.id];

    return (
      <div key={field.id} className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>

        {field.type === "dropdown" ? (
          <select value={value} onChange={(e) => handleChange(field.id, e.target.value)} className={baseInputClass}>
            <option value="">{t("leave.select_placeholder")}</option>
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : field.type === "textarea" ? (
          <textarea value={value} onChange={(e) => handleChange(field.id, e.target.value)} rows={4} className={baseInputClass} />
        ) : field.type === "file" ? (
          <div className="space-y-1">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
              onChange={(e) => handleFileChange(field.id, e.target.files?.[0] ?? null)}
              className={`w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 border rounded-lg px-2 py-1.5 ${
                error ? "border-red-300" : "border-gray-300"
              }`}
            />
            {pendingFile && <p className="text-xs text-green-600">✓ {pendingFile.name}</p>}
          </div>
        ) : (
          <input
            type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={baseInputClass}
          />
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    );
  };

  // ── Guards ──────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="mt-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">{t("leave.application_submitted")}</h2>
          <p className="text-sm text-gray-500 mb-6">{t("leave.application_submitted_desc")}</p>
          <Button onClick={() => navigate("/leave")}>{t("leave.back_to_leave")}</Button>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <button
          type="button"
          onClick={() => {
            if (step === "fill_form" && !urlTemplateId && templateList.length > 1) {
              setStep("select_template");
              setTemplate(null);
            } else {
              navigate("/leave");
            }
          }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 active:text-gray-800 p-2 -ml-2 rounded-lg mb-2 md:mb-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {step === "fill_form" && !urlTemplateId && templateList.length > 1
            ? t("leave.back_to_form_selection")
            : t("leave.title")}
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          {t("leave.leave_application")}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t("leave.leave_application_subtitle")}</p>
      </div>

      {/* On Behalf section (officer only, shown on both steps) */}
      {isOfficer && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-4 space-y-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isOnBehalf}
              onChange={(e) => handleOnBehalfToggle(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-700">{t("leave.on_behalf")}</span>
          </label>

          {!isOnBehalf && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-amber-700">{t("leave.self_submit_warning")}</p>
            </div>
          )}

          {isOnBehalf && (
            <div className="space-y-3 pl-6">
              <CompanySearchSelect
                value={selectedCompanyId}
                onChange={(companyId) => handleCompanyChange(companyId)}
                label={t("leave.select_company")}
                required
                feature="company_form"
              />
              {step === "fill_form" && (
                <UserSearchSelect
                  companyId={selectedCompanyId}
                  value={selectedUserId}
                  onChange={(userId) => {
                    setSelectedUserId(userId);
                    if (errors["_targetUser"]) {
                      setErrors((prev) => { const u = { ...prev }; delete u["_targetUser"]; return u; });
                    }
                  }}
                  label={t("leave.select_user")}
                  required
                />
              )}
              {errors["_targetUser"] && (
                <p className="text-sm text-red-600">{errors["_targetUser"]}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Step 1: Template Selection ── */}
      {step === "select_template" && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">{t("leave.select_form")}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{t("leave.select_form_desc")}</p>
          </div>

          {listLoading ? (
            <div className="py-10"><Spinner /></div>
          ) : templateList.length === 0 ? (
            <div className="py-10">
              <EmptyState title={t("leave.no_form_title")} description={t("leave.no_form_desc")} />
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {templateList.map((tpl) => (
                <li key={tpl.template_id}>
                  <button
                    type="button"
                    onClick={() => handleSelectTemplate(tpl.template_id)}
                    className="w-full flex items-center gap-3 px-4 md:px-6 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{tpl.title || t("leave.untitled_form")}</p>
                      {tpl.description && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{tpl.description}</p>
                      )}
                      {tpl.category && (
                        <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
                          {tpl.category}
                        </span>
                      )}
                    </div>
                    <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Step 2: Fill Form ── */}
      {step === "fill_form" && (
        <>
          {loading ? (
            <Spinner />
          ) : !template ? (
            <EmptyState title={t("leave.no_form_title")} description={t("leave.no_form_desc")} />
          ) : (
            <>
              {(template.title || template.description) && (
                <div className="mb-4">
                  {template.title && (
                    <h2 className="text-lg md:text-xl font-bold text-gray-900">{template.title}</h2>
                  )}
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                  )}
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isOnBehalf && loading ? (
                    <Spinner />
                  ) : (
                    template.fields.map(renderField)
                  )}

                  {submitError && <p className="text-sm text-red-600">{submitError}</p>}

                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end pt-2 border-t border-gray-200">
                    <Button variant="secondary" type="button" onClick={() => navigate("/leave")}>
                      {t("leave.cancel")}
                    </Button>
                    <Button type="submit" loading={submitting}>
                      {t("leave.submit_application")}
                    </Button>
                  </div>
                </form>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
